"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Link2 } from "lucide-react";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { cn } from "@/lib/utils";
import {
  formatEntityTypeForDisplay,
  getLinkedEntityHref,
  getLinkedEntityTitle,
  normalizeEntityTypeForApi,
} from "@/lib/utils/crm/linked-entity";
import { isUUID } from "@/components/shared/UniversalCRMTable/utils/unified-field-renderer";

async function fetchEntityRowByIds(
  apiEntityType: string,
  entityId: string
): Promise<Record<string, unknown> | null> {
  const supabase = getBrowserSupabaseClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return null;

  const res = await fetch(`/api/entities/${apiEntityType}/by-ids`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ ids: [entityId], ttl: 600 }),
  });

  if (!res.ok) return null;
  const json: { success?: boolean; data?: Record<string, unknown>[] } = await res.json();
  if (!json.success || !Array.isArray(json.data) || json.data.length === 0) return null;
  const row = json.data[0];
  return typeof row === "object" && row !== null ? (row) : null;
}

export interface SmartLinkedEntityDisplayProps {
  entityType: string | null | undefined;
  entityId: string | null | undefined;
  className?: string;
  disabled?: boolean;
}

export function SmartLinkedEntityDisplay({
  entityType,
  entityId,
  className = "",
  disabled = false,
}: SmartLinkedEntityDisplayProps) {
  const apiType = useMemo(() => normalizeEntityTypeForApi(entityType), [entityType]);
  const idStr = entityId != null && entityId !== "" ? String(entityId) : "";

  const enabled = Boolean(apiType && idStr && isUUID(idStr));

  const { data: row, isLoading } = useQuery({
    queryKey: ["linked-entity-row", apiType, idStr],
    queryFn: () => fetchEntityRowByIds(apiType!, idStr),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const typeLabel = formatEntityTypeForDisplay(entityType ?? undefined);

  const title = useMemo(() => {
    if (!enabled) {
      if (idStr && isUUID(idStr)) return `${idStr.slice(0, 8)}…`;
      return idStr || "—";
    }
    if (row) return getLinkedEntityTitle(row, apiType!);
    if (isLoading) return "Loading…";
    return `${idStr.slice(0, 8)}…`;
  }, [apiType, enabled, idStr, isLoading, row]);

  const href = enabled && apiType ? getLinkedEntityHref(apiType, idStr) : null;

  if (!idStr) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  if (!enabled) {
    const short = idStr.length > 10 ? `${idStr.slice(0, 8)}…` : idStr;
    return (
      <span
        className={cn("text-sm text-muted-foreground font-mono truncate max-w-[200px] inline-block", className)}
        title={idStr}
      >
        {short}
      </span>
    );
  }

  if (disabled) {
    return (
      <div className={cn("flex items-center gap-2 min-w-0", className)}>
        <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm truncate">{title}</span>
      </div>
    );
  }

  const popoverBody = (
    <div className="space-y-3 p-1 max-w-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{typeLabel}</Badge>
      </div>
      <p className="text-sm font-medium leading-snug break-words">{title}</p>
      <p className="text-xs font-mono text-muted-foreground break-all">{idStr}</p>
      {href ? (
        <Button variant="outline" size="sm" asChild className="w-full">
          <Link href={href} onClick={(e) => e.stopPropagation()}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Open record
          </Link>
        </Button>
      ) : null}
    </div>
  );

  return (
    <ResponsivePopover
      content={popoverBody}
      title="Linked record"
      align="start"
      popoverSide="bottom"
      withinPortal={true}
    >
      <button
        type="button"
        className={cn(
          "flex items-center gap-2 min-w-0 max-w-full text-left rounded px-1 py-0.5",
          "hover:bg-muted/80 cursor-default sm:cursor-pointer",
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm text-primary truncate underline-offset-2 hover:underline">{title}</span>
      </button>
    </ResponsivePopover>
  );
}
